import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthProvider'
import { publicApi, api } from '../../lib/api'
import { US_STATES } from '../../lib/us-states'
import { usePageMeta } from '../../hooks/usePageMeta'
import ParishCard from '../../components/ParishCard'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Search, Plus, Star, Loader2, X } from 'lucide-react'

export default function ParishBrowse() {
  const navigate = useNavigate()
  const { session, user } = useAuth()

  usePageMeta({
    title: 'Find Your Parish — Claritas',
    description: 'Search Catholic parishes and give securely, with or without an account.',
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [parishes, setParishes] = useState([])
  const [meta, setMeta] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [addingParish, setAddingParish] = useState(null)
  const [error, setError] = useState(null)
  const [userParishes, setUserParishes] = useState([])

  const searchTimeoutRef = useRef(null)
  const requestIdRef = useRef(0)

  // Fetch user's parishes for "already added" checks (logged-in only)
  useEffect(() => {
    if (session && user?.id) {
      fetchUserParishes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, user?.id])

  // Initial load
  useEffect(() => {
    fetchParishes(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch when state filter changes
  useEffect(() => {
    fetchParishes(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const fetchUserParishes = async () => {
    try {
      const userData = await api.getUser()
      setUserParishes(userData.parishes || [])
    } catch (err) {
      console.error('Failed to fetch user parishes:', err)
    }
  }

  // `search` is passed explicitly from handlers that run before React has
  // re-rendered with the new query; reading state there gives the old value.
  const fetchParishes = async (page = 1, append = false, search = searchQuery) => {
    const requestId = ++requestIdRef.current

    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const response = await publicApi.searchParishes({
        page,
        limit: 20,
        search: search.trim() || undefined,
        state: stateFilter || undefined,
      })

      // Discard stale responses
      if (requestId !== requestIdRef.current) return

      if (append) {
        setParishes(prev => [...prev, ...(response.data || [])])
      } else {
        setParishes(response.data || [])
      }
      setMeta(response.meta || null)
      setCurrentPage(page)
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      if (!append) {
        setParishes([])
        setMeta(null)
      }
      setError(err.message || 'Failed to search parishes')
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchParishes(1, false, value)
    }, 500)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      fetchParishes(1, false)
    }
  }

  const handleLoadMore = () => {
    fetchParishes(currentPage + 1, true)
  }

  const clearFilters = () => {
    setSearchQuery('')
    setStateFilter('')
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    // Clearing an already-empty state filter doesn't re-run its effect
    if (!stateFilter) fetchParishes(1, false, '')
  }

  const handleAddParish = async (parishId) => {
    try {
      setAddingParish(parishId)
      setError(null)

      const makePrimary = userParishes.length === 0
      await api.updateUserParish(user.id, parishId, makePrimary)
      await fetchUserParishes()
    } catch (err) {
      console.error('Failed to add parish:', err)
      setError(err.message || 'Failed to add parish')
    } finally {
      setAddingParish(null)
    }
  }

  const isParishAdded = (parishId) => {
    return userParishes.some(p => p.parishId === parishId)
  }

  const hasActiveFilters = searchQuery || stateFilter

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-display text-charcoal">
          Find a Parish
        </h1>
        <p className="text-charcoal-muted mt-1">
          Search by name, city, or state to find your parish.
        </p>
      </div>

      {/* Search Controls - Sticky */}
      <div className="bg-white border border-surface-border rounded p-4 mb-6 sticky top-0 z-10">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-faint" />
            <Input
              type="text"
              placeholder="Search by name or city..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="pl-10"
            />
          </div>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="h-10 rounded border border-surface-border bg-white px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold sm:w-48"
          >
            <option value="">All States</option>
            {US_STATES.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Result count & clear filters */}
        {meta && !loading && (
          <div className="mt-3 flex items-center justify-between text-sm text-charcoal-muted">
            <span>
              {meta.totalItems} {meta.totalItems === 1 ? 'parish' : 'parishes'} found
            </span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-gold hover:text-gold-light transition-colors duration-150"
              >
                <X className="w-3.5 h-3.5" />
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : parishes.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {parishes.map((parish) => {
              const isAdded = isParishAdded(parish.id)
              const isAdding = addingParish === parish.id

              return (
                <div key={parish.id} className="relative h-full">
                  <ParishCard
                    parish={parish}
                    onClick={() => navigate(`/parishes/${parish.id}`)}
                  />
                  {session && (
                    <div className="absolute top-4 right-4">
                      {isAdded ? (
                        <div className="flex items-center gap-1.5 bg-gold/15 px-2 py-0.5 rounded-sm">
                          <Star className="w-3.5 h-3.5 text-gold fill-gold" />
                          <span className="text-xs text-gold font-semibold tracking-wide">Added</span>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddParish(parish.id)}
                          disabled={isAdding}
                          className="bg-white/90"
                        >
                          {isAdding ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Load More */}
          {meta?.hasNextPage && (
            <div className="mt-8 text-center">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="min-w-[200px]"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Load More Parishes
              </Button>
              <p className="text-xs text-charcoal-faint mt-2">
                Showing {parishes.length} of {meta.totalItems}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <Search className="w-12 h-12 text-charcoal-faint mx-auto mb-4" />
          <p className="text-charcoal-muted mb-2">
            No parishes found.
          </p>
          {hasActiveFilters && (
            <p className="text-sm text-charcoal-subtle">
              Try adjusting your search or{' '}
              <button onClick={clearFilters} className="text-gold hover:underline">
                clear all filters
              </button>.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
